import React from "react";
import { sortBy } from "lodash";
import { useHistory, useParams } from "react-router";
import { OBJECT_TYPE, WORKFLOW_STEP_STATUS } from "common/constants";
import { buildLink } from "common/routing";
import { getObjectDisplay } from "common-client/utils/customObject";
import { useGetWorkflowQuery } from "../../../generated/graphql";
import { spacing } from "../../../stitches.config";
import { setTitle } from "../../../utils/title";
import { FlexColumn, FlexRow } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import { ProgressBar } from "../../Common/ProgressBar";
import { StyleVariant, Tag } from "../../Common/Tag";
import { Body, Title } from "../../Common/Typography";
import { Loading } from "../__styles__/Guest";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { accountTitle } from "../utils";
import { ApplicationInfo } from "./__styles__/Workflow";
import { WorkflowStep } from "./WorkflowStep";

export const Workflow = () => {
  const history = useHistory();
  const { workflowId } = useParams<{
    workflowId: string;
  }>();

  const { data, loading } = useGetWorkflowQuery({
    variables: { workflowId },
    fetchPolicy: "network-only",
  });

  const title = data?.workflow?.workflowDefinition.name ?? "Loading...";
  setTitle(`${title} | ${accountTitle()}`);

  if (loading) {
    return <Loading />;
  }

  if (!data?.workflow) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.WORKFLOW} />;
  }

  const { workflow } = data;

  const workflowName = workflow.workflowDefinition.name;
  const objectDisplay = workflow.property
    ? getObjectDisplay({
        objectType: OBJECT_TYPE.PROPERTY,
        object: workflow.property,
      })
    : null;

  const orderedWorkflowSteps = sortBy(
    workflow.workflowSteps,
    "workflowStepDefinition.order"
  );

  const completedCount = workflow.workflowSteps.filter(
    step =>
      step.status &&
      [
        WORKFLOW_STEP_STATUS.FAILED,
        WORKFLOW_STEP_STATUS.SUCCEEDED,
        WORKFLOW_STEP_STATUS.SKIPPED,
      ].includes(step.status)
  ).length;

  const totalSteps = workflow.workflowSteps.length;

  return (
    <Layout hasGutters={true}>
      <FlexColumn
        style={{ margin: `${spacing.l.value} 0`, gap: spacing.l.value }}
      >
        <Button
          styleVariant="buttonLink"
          leftIconName="chevron-left"
          iconSpacing="xs"
          onClick={() =>
            history.push(
              buildLink("listWorkflows", {
                workflowDefinitionId: workflow.workflowDefinition.id,
              })
            )
          }
          fitContent
        >
          {workflowName}
        </Button>
        <ApplicationInfo>
          <Body size="small" type="emphasis" color="contentSecondary">
            APPLICATION
          </Body>
          <FlexColumn style={{ gap: spacing.s.value }}>
            <FlexRow style={{ gap: spacing.s.value, alignItems: "center" }}>
              {objectDisplay && (
                <Title size="default" type="semiBold">
                  {objectDisplay}
                </Title>
              )}
              <Tag styleVariant={workflow.status.display as StyleVariant}>
                {workflow.status.text}
              </Tag>
            </FlexRow>
          </FlexColumn>
          {workflow.property && (
            <Button
              styleVariant="secondary"
              size="small"
              fitContent
              onClick={() =>
                history.push(
                  buildLink("residentProperty", {
                    propertyId: workflow.property!.id,
                  })
                )
              }
            >
              View property
            </Button>
          )}
        </ApplicationInfo>
        <ProgressBar value={completedCount} max={totalSteps} />
        <FlexColumn style={{ gap: spacing.m.value }}>
          {orderedWorkflowSteps.map(step => (
            <WorkflowStep
              key={step.id}
              workflowStep={step}
              objectId={workflow.property?.id}
              objectType={
                workflow.property?.id ? OBJECT_TYPE.PROPERTY : undefined
              }
            />
          ))}
        </FlexColumn>
      </FlexColumn>
    </Layout>
  );
};
